import { matchPath } from 'react-router-dom';
import type { PortalModule, PortalRouteHandle, PortalTab } from './types';
import { personaRank, type PortalPersona } from '../shared/persona';

export const RESERVED_MODULE_IDS = [
  'login',
  'releases',
  'admin',
  'account',
  'api',
  'ws',
  'assets',
] as const;

export function moduleIdProblem(id: string): string | null {
  if (!id) return 'module id 不能为空';
  if (!/^[a-z][a-z0-9-]*$/.test(id)) return `module id 只能是小写字母、数字和连字符：${id}`;
  if ((RESERVED_MODULE_IDS as readonly string[]).includes(id)) return `module id 是保留字：${id}`;
  return null;
}

export function validateModules(modules: PortalModule[]): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const module of modules) {
    const problem = moduleIdProblem(module.id);
    if (problem) problems.push(problem);
    if (seen.has(module.id)) problems.push(`module id 重复：${module.id}`);
    seen.add(module.id);
  }
  return problems;
}

export function visibleModules(modules: PortalModule[], persona: PortalPersona): PortalModule[] {
  const rank = personaRank(persona);
  return modules
    .filter((module) => module.nav.show)
    .filter((module) => module.status !== 'archived')
    .filter((module) => rank >= personaRank(module.minRole ?? 'visitor'))
    .sort((a, b) => a.nav.order - b.nav.order || a.title.localeCompare(b.title));
}

export function tabIsActive(tab: PortalTab, pathname: string): boolean {
  if (pathname === tab.to) return true;
  return (tab.matches ?? []).some(
    (match) => pathname === match || pathname.startsWith(`${match}/`),
  );
}

export function activeTab(module: PortalModule, pathname: string): PortalTab | null {
  return (module.tabs ?? []).find((tab) => tabIsActive(tab, pathname)) ?? null;
}

export function activeModuleId(modules: PortalModule[], pathname: string): string | null {
  const match = modules.find(
    (module) => pathname === `/${module.id}` || pathname.startsWith(`/${module.id}/`),
  );
  return match?.id ?? null;
}

export function mainClassForPath(modules: PortalModule[], pathname: string): string | undefined {
  let mainClass: string | undefined;
  for (const module of modules) {
    for (const route of [...(module.publicRoutes ?? []), ...(module.authRoutes ?? [])]) {
      if (!route.path || route.path === '*') continue;
      if (!matchPath({ path: `/${module.id}/${route.path}`, end: true }, pathname)) continue;
      const handle = route.handle as PortalRouteHandle | undefined;
      if (handle?.mainClass) mainClass = handle.mainClass;
    }
  }
  return mainClass;
}